import React, { useState, useRef, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { AudioControl } from './components/AudioControl';
import { LanguageSelector } from './components/LanguageSelector';
import { UserBadge } from './components/LoginSystem';
import { useUserAnalytics } from './hooks/useUserAnalytics';
import { MessageHistory } from './components/MessageHistory';
import { Language, Message } from './config/types';
import { labels, structuredData, MAX_HISTORY } from './config/constants';
import LoginSystem from './components/LoginSystem';
import MSALSingleton from './utils/msalInstance';
import { sendMessage } from './utils/sendMessages';

const App: React.FC = () => {
  const [prompt, setPrompt] = useState('');
  const [language, setLanguage] = useState<Language>('en');
  const [isMusicPlaying, setIsMusicPlaying] = useState(false);
  const [response, setResponse] = useState<string>('');
  const [firstMessageSubmitted, setFirstMessageSubmitted] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [messages, setMessages] = useState<Message[]>([]);

  const audioRef = useRef<HTMLAudioElement | null>(null);
  const analyticsResponse = useUserAnalytics(language);

  useEffect(() => {
    MSALSingleton.initialize().catch(console.error);
    const path = window.location.pathname.substring(1) as Language;
    if (path === 'fr' || path === 'it') {
      setLanguage(path);
    }
  }, []);

  useEffect(() => {
    const newPath = language === 'en' ? '/' : `/${language}`;
    window.history.pushState({}, '', newPath);
  }, [language]);

  useEffect(() => {
    if (!audioRef.current) return;
    if (isMusicPlaying) {
      audioRef.current.play();
    } else {
      audioRef.current.pause();
    }
  }, [isMusicPlaying]);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!prompt.trim()) return;

    setIsLoading(true);
    setError('');

    try {
      const result = await sendMessage({
        prompt: prompt.trim(),
        history: messages
      });

      setResponse(result.assistantMessage.content);
      setMessages(prev => [...prev, result.message, result.assistantMessage].slice(-MAX_HISTORY));
      setFirstMessageSubmitted(true);
    } catch (err: any) {
      console.error('API Error: ', err);
      setError(err.message || 'An unexpected error occurred');
    } finally {
      setIsLoading(false);
      setPrompt('');
    }
  };

  return (
    <>
      <Helmet>
        <html lang={language} />
        <title>{labels[language].metaTitle}</title>
        <meta name="description" content={labels[language].metaDescription} />
        <meta name="keywords" content="AI meditation, mindfulness app, zen artificial intelligence, digital wellness, meditation assistant, spiritual guidance, AI wellness coach" />
        <meta property="og:title" content={labels[language].metaTitle} />
        <meta property="og:description" content={labels[language].metaDescription} />
        <meta property="og:type" content="website" />
        <meta property="og:image" content="/assets/zenai-og-image.jpg" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={labels[language].metaTitle} />
        <meta name="twitter:description" content={labels[language].metaDescription} />
        <meta name="twitter:image" content="/assets/zenai-og-image.jpg" />
        <meta name="robots" content="index, follow" />
        <meta name="viewport" content="width=device-width, initial-scale=1.0" />
        <link rel="canonical" href={`https://zenai.app${language === 'en' ? '' : `/${language}`}`} />
        <link rel="alternate" hrefLang="en" href="https://zenai.app" />
        <link rel="alternate" hrefLang="fr" href="https://zenai.app/fr" />
        <link rel="alternate" hrefLang="it" href="https://zenai.app/it" />
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      </Helmet>

      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-start justify-center p-6 font-sans relative">
        <video autoPlay loop muted className="absolute inset-0 w-full h-full object-cover z-0">
          <source src="/assets/zen-video.mp4" type="video/mp4" />
        </video>

        <audio ref={audioRef} src="/assets/zen-music.mp3" loop />

        <div className="w-full max-w-2xl relative z-10 mt-8">
          <div className="bg-white rounded-2xl shadow-2xl overflow-hidden border border-gray-200">
            <div className="bg-gradient-to-r from-white to-gray-50 border-b border-gray-200 py-4 px-6 flex items-center justify-between">
              <div className="flex space-x-2">
                <div className="w-3 h-3 bg-red-500 rounded-full" />
                <div className="w-3 h-3 bg-yellow-500 rounded-full" />
                <div className="w-3 h-3 bg-green-500 rounded-full" />
              </div>

              <h1 className="text-xl font-semibold text-gray-800">Zen AI</h1>

              <div className="flex space-x-2">
                <AudioControl
                  isPlaying={isMusicPlaying}
                  onToggle={() => setIsMusicPlaying(prev => !prev)}
                />
                <LanguageSelector
                  currentLanguage={language}
                  onChange={() => {
                    setLanguage((prev: Language) => {
                      switch (prev) {
                        case 'en': return 'fr';
                        case 'fr': return 'it';
                        case 'it': return 'en';
                        default: return 'en';
                      }
                    });
                  }}
                />
                <UserBadge />
              </div>
            </div>

            <div className="p-6">
              <form onSubmit={handleSubmit} className="space-y-4">
                <textarea
                  value={prompt}
                  onChange={e => setPrompt(e.target.value)}
                  onKeyDown={e => {
                    if (e.key === 'Enter' && !e.altKey) {
                      e.preventDefault();
                      handleSubmit(e);
                    }
                  }}
                  className="w-full p-4 border rounded-lg resize-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder={firstMessageSubmitted ? 'Enter your message here...' : analyticsResponse}
                  rows={4}
                />
                <button
                  type="submit"
                  disabled={isLoading}
                  className={`w-full font-semibold rounded-lg shadow-md 
    text-white 
    ${isLoading
                      ? 'bg-gradient-to-r from-green-400 via-green-700 to-green-400 bg-size-200 animate-gradient-loading'
                      : 'bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700'}
    transition-all duration-300 ease-in-out transform hover:scale-[1.02]
    disabled:opacity-50 disabled:cursor-not-allowed
    py-2 px-4 relative overflow-hidden`}
                >
                  {isLoading ? labels[language].generatingWisdom : labels[language].submit}
                </button>
              </form>

              {error && (
                <div className="mt-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg" role="alert">
                  <p className="text-sm">{error}</p>
                </div>
              )}

              {response && (
                <div className="mt-4 border rounded-lg bg-gray-50 p-4">
                  <h2 className="text-lg font-semibold mb-2 text-gray-800">
                    {labels[language].mindfulInsight}
                  </h2>
                  <p className="text-gray-700 whitespace-pre-line">{response}</p>
                </div>
              )}

              <MessageHistory messages={messages} language={language} />

              <footer className="mt-8 text-center text-gray-500 text-sm">
                <p>Breathe. Ask. Understand.</p>
                <LoginSystem />
              </footer>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default App;